import { createEffect, onMount } from 'solid-js'
import { editingTextId, setEditingTextId } from '../../editor.ts'
import {
  endCommit,
  beginCommit,
  isCommitOpen,
  removeNodes,
  updateNode,
} from '../../scene/store.ts'
import type { TextNode } from '../../scene/types.ts'

export function TextView(props: { node: TextNode }) {
  let el!: HTMLDivElement
  const editing = () => editingTextId() === props.node.id

  /** Push the rendered size back into the model so selection bounds match. */
  const measure = () => {
    if (!props.node.autoWidth) return
    const w = Math.max(1, el.offsetWidth)
    const h = Math.max(1, el.offsetHeight)
    if (w !== props.node.w || h !== props.node.h) updateNode(props.node.id, { w, h })
  }

  onMount(measure)

  // The DOM owns the text while editing; outside of that the model does, so
  // undo and project loads have to be mirrored back in.
  createEffect(() => {
    const text = props.node.text
    if (editing()) return
    if (el.innerText !== text) {
      el.textContent = text
      measure()
    }
  })

  createEffect(() => {
    if (!editing()) return
    // Editing an existing node opens its own commit; the text tool already
    // opened one so that create-then-type is a single undo step.
    if (!isCommitOpen()) beginCommit([props.node.id])
    el.focus()
    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)
    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(range)
  })

  const onInput = () => {
    updateNode(props.node.id, { text: el.innerText })
    measure()
  }

  const onBlur = () => {
    if (!editing()) return
    const text = el.innerText
    setEditingTextId(null)
    if (text.trim() === '') {
      removeNodes([props.node.id])
      endCommit('Delete text')
    } else {
      endCommit('Text')
    }
  }

  const onKeyDown = (e: KeyboardEvent) => {
    // Global shortcuts must not fire while typing.
    e.stopPropagation()
    if (e.key === 'Escape') {
      e.preventDefault()
      el.blur()
    }
  }

  return (
    <div
      ref={el}
      class="text"
      classList={{ editing: editing() }}
      contenteditable={editing() ? 'plaintext-only' : false}
      spellcheck={false}
      onInput={onInput}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      onPointerDown={(e) => {
        if (editing()) e.stopPropagation()
      }}
      style={{
        'font-size': `${props.node.fontSize}px`,
        'font-family': props.node.fontFamily,
        color: props.node.color,
        'text-align': props.node.align,
        'white-space': props.node.autoWidth ? 'pre' : 'pre-wrap',
        width: props.node.autoWidth ? 'max-content' : '100%',
        height: props.node.autoWidth ? 'max-content' : '100%',
      }}
    />
  )
}
